import type { ReactNode } from 'react';

type Variant = 'primary' | 'secondary';
type Size = 'md' | 'lg';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
}

interface LinkProps extends CommonProps {
  href: string;
  onClick?: never;
  /** Optional: surface "(placeholder)" intent to assistive tech without visual noise. */
  ariaLabel?: string;
}

interface ButtonProps extends CommonProps {
  href?: never;
  onClick: () => void;
  ariaLabel?: string;
}

type Props = LinkProps | ButtonProps;

const base =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold ' +
  'transition-transform transition-colors duration-150 active:scale-[0.98] ' +
  'min-h-[44px]'; // WCAG-friendly tap target

const sizes: Record<Size, string> = {
  md: 'px-5 py-2.5 text-base',
  lg: 'px-7 py-3.5 text-lg',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-accent text-cream-50 shadow-sm shadow-espresso/20 hover:bg-accent-dark',
  secondary:
    'bg-cream-50 text-espresso ring-2 ring-inset ring-mocha/30 hover:bg-cream-100',
};

/**
 * Shared, accessible CTA. Renders a real `<a>` when given `href` (navigation /
 * anchor) or a `<button>` when given `onClick` (e.g. open the bot panel), so
 * keyboard + screen-reader semantics are always correct.
 */
export function CtaButton(props: Props) {
  const {
    variant = 'primary',
    size = 'md',
    children,
    className = '',
    ariaLabel,
  } = props;

  const classes = `${base} ${sizes[size]} ${variants[variant]} ${className}`;

  if ('href' in props && props.href !== undefined) {
    return (
      <a href={props.href} aria-label={ariaLabel} className={classes}>
        {children}
      </a>
    );
  }

  return (
    <button type="button" onClick={props.onClick} aria-label={ariaLabel} className={classes}>
      {children}
    </button>
  );
}
